/**
 * Pure parsing for the request-detail sheet (TRA-1412): turns one saved
 * `TraceDocument` into the readable sections the sheet renders.
 *
 * The chat-body parsing itself (messages, tool calls, streamed SSE
 * responses) is the review transcript's; this file only arranges the
 * result for a single call. Nothing here touches the DOM, so the sheet's
 * logic is testable with made-up documents.
 */

import type { TraceBody, TraceDocument } from '@/api/generated/model'
import {
  isSseBody,
  jsonDisplaySegments,
  jsonObjectEntries,
  parseMessage,
  parseResponseMessage,
  ssePayloads,
  type RawMessage,
} from '@/components/review/transcript'
import { isRecord } from '@/lib/guards'

/** One top-level request field the gateway forwarded, as its source JSON. */
export interface ParamEntry {
  key: string
  value: string
}

/** One tool schema from the request's `tools` array. */
export interface ToolSchema {
  name: string
  /** The whole tool entry, pretty-printed. */
  schema: string
}

/** A body the gateway did not keep: tracing off, or over the capture cap. */
interface MissingBody {
  kind: 'missing'
  truncated: boolean
}

/** A body that is not a chat completion; the raw section is the only view. */
interface UnrecognisedBody {
  kind: 'raw'
  truncated: boolean
}

export interface ParsedRequestBody {
  kind: 'request'
  truncated: boolean
  params: ParamEntry[]
  tools: ToolSchema[]
  messages: RawMessage[]
}

export interface ParsedResponseBody {
  kind: 'response'
  truncated: boolean
  message: RawMessage
  finishReason: string | null
}

export type RequestBodyState = MissingBody | UnrecognisedBody | ParsedRequestBody
export type ResponseBodyState = MissingBody | UnrecognisedBody | ParsedResponseBody

/** Fields rendered as their own sections rather than as params. */
const NON_PARAM_FIELDS = new Set(['messages', 'tools'])

function parseJson(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return undefined
  }
}

function parseTools(value: unknown): ToolSchema[] {
  if (!Array.isArray(value)) return []
  return value.map((tool): ToolSchema => {
    const fn = isRecord(tool) && isRecord(tool.function) ? tool.function : {}
    return {
      name: typeof fn.name === 'string' && fn.name !== '' ? fn.name : 'tool',
      schema: JSON.stringify(tool, null, 2),
    }
  })
}

/**
 * The params as the gateway forwarded them. Read from the source text,
 * not from the parsed object: a parse-and-stringify round trip rounds a
 * number past 2^53, and a `seed` can be one.
 */
function parseParams(bodyText: string): ParamEntry[] {
  const entries = jsonObjectEntries(bodyText) ?? new Map<string, string>()
  return [...entries]
    .filter(([key]) => !NON_PARAM_FIELDS.has(key))
    .map(([key, value]) => ({ key, value }))
}

/** The request body as the gateway forwarded it: params, tools, messages. */
export function parseRequestBody(body: TraceBody): RequestBodyState {
  const truncated = body.truncated === true
  if (body.bodyText == null) return { kind: 'missing', truncated }
  const parsed = parseJson(body.bodyText)
  if (!isRecord(parsed) || !Array.isArray(parsed.messages)) return { kind: 'raw', truncated }
  const messages = parsed.messages.map(parseMessage)
  if (messages.some((message) => message === null)) return { kind: 'raw', truncated }
  return {
    kind: 'request',
    truncated,
    params: parseParams(body.bodyText),
    tools: parseTools(parsed.tools),
    messages: messages as RawMessage[],
  }
}

/**
 * The finish reason of a chat completion. A streamed response carries it
 * on whichever frame ends the choice, so every frame is checked; the
 * last one seen wins.
 */
function parseFinishReason(bodyText: string): string | null {
  const payloads = isSseBody(bodyText) ? ssePayloads(bodyText) : [bodyText]
  let reason: string | null = null
  for (const payload of payloads) {
    const frame = parseJson(payload)
    if (!isRecord(frame) || !Array.isArray(frame.choices)) continue
    const choice: unknown = frame.choices[0]
    if (isRecord(choice) && typeof choice.finish_reason === 'string') {
      reason = choice.finish_reason
    }
  }
  return reason
}

/** The response body as one assistant message, streamed or not. */
export function parseResponseBody(body: TraceBody): ResponseBodyState {
  const truncated = body.truncated === true
  if (body.bodyText == null) return { kind: 'missing', truncated }
  const message = parseResponseMessage(body.bodyText)
  if (message === null) return { kind: 'raw', truncated }
  return {
    kind: 'response',
    truncated,
    message,
    finishReason: parseFinishReason(body.bodyText),
  }
}

/**
 * How many earlier messages of the run this call sent that are NOT in its
 * saved body. The gateway keeps only `messages[fromIndex:]` of a later
 * turn; the rest live with the run's earlier calls.
 */
export function earlierMessageCount(doc: TraceDocument): number {
  return doc.requestDelta?.fromIndex ?? 0
}

/** The whole-run page for this call, or null when it is not part of one. */
export function wholeRunPath(doc: TraceDocument, traceKey: string): string | null {
  if (earlierMessageCount(doc) === 0 && doc.conversationId == null) return null
  return `/conversations?trace_key=${encodeURIComponent(traceKey)}`
}

/**
 * A raw body for the folded Raw sections: reindented when it is JSON,
 * verbatim otherwise. Token text is never re-encoded, so 64-bit ids
 * survive. Called only once the section is opened; a 10 MB body is not
 * lexed for a section nobody looks at.
 */
export function formatRawBody(bodyText: string): string {
  const segments = jsonDisplaySegments(bodyText)
  if (segments === null) return bodyText
  return segments.map((segment) => segment.text).join('')
}

/** Text longer than this is clipped in a message card until "Show all". */
export const CLIP_LENGTH = 4000
